import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { DroneEntity, DroneStatusType, OrderEntity } from '@src/core/features';
import { useDronesPageDrones } from '@src/features/drone/hooks/useDronesPageDrones';
import { useFlightProvider } from '@src/features/order/provider';
import { useGetOrderMissionQuery, useGetOrderQuery } from '@src/features/order/redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { MapLocationEntity } from '@src/features/drone/redux/state';
import { selectEndedMissionSerials, selectMapLocation, selectOptimisticMissionSerials, selectSelectedDrone } from '@src/features/drone/redux/selectors';
import { addEndedMissionSerial, removeEndedMissionSerials, removeOptimisticMissionSerials, setSelectedDrone } from '@src/features/drone/redux/actions';
import { setMapLocation } from '@src/core/controllers';
import { useDroneSelection } from '@src/features/drone/hooks/useDroneSelection';
import { useFlyingDrones } from '@src/features/drone/hooks/useFlyingDrones';
import { useDroneRotations } from '@src/features/drone/hooks/useDroneRotations';
import { usePrevious } from '@src/features/drone/hooks/usePrevious';
import { dronesPageDroneSerialFromUrl } from '@src/features/drone/utils/select-drone-on-map';
import { trustedMissionIdFromDrone } from '@src/features/drone/utils/trusted-mission-id-from-drone';
import { missionIdFromOrderForActiveRoute } from '@src/features/order/utils/mission-id-from-order-for-route';
import { urlMissionIdForDronesRoute } from '@src/features/drone/utils/url-mission-id-for-drones-route';
import { isSerialInMissionBucket } from '@src/features/drone/utils/find-drone-by-serial';
import {
  missionDataMatchesDrone,
  resolveDronesMapMode,
  resolveEffectiveMissionId,
  type DronesMapMode,
} from '@src/features/drone/utils/drones-route-mission';
import {
  isUpdateRequiredDrone,
  isUpdateRequiredStatus,
} from '@src/features/drone/utils/operational-drones';

/**
 * Safety net for the optimistic End Mission clear: if REST never confirms IDLE within this window
 * (e.g. the command failed), drop the end-mission suppression so the UI falls back to real data
 * instead of showing the drone idle forever. Comfortably longer than the normal UPDATE_DRONES lag.
 */
const END_MISSION_SUPPRESS_TIMEOUT_MS = 30_000;

export type DronesPageViewModel = {
  allDrones: DroneEntity[];
  idleDrones: DroneEntity[];
  missionDrones: DroneEntity[];
  updateRequiredDrones: DroneEntity[];
  flyingDrones: DroneEntity[];
  selectedFromRedux: DroneEntity | null;
  selectedDrone: DroneEntity | null;
  selectedSerial: string | null;
  location?: MapLocationEntity;
  mapMode: DronesMapMode;
  effectiveMissionId: string | null;
  sidebarMissionId: string | null;
  orderId: string | number | null;
  order: OrderEntity | undefined;
  isOrderLoading: boolean;
  isOnMission: boolean;
  isStrictMission: boolean;
  isFlying: boolean;
  isOptimisticallyOnMission: boolean;
  prevIsFlying: boolean;
  rotations: Record<string, number | undefined>;
  enRouteFallbackPosition: google.maps.LatLngLiteral | null;
  displayCreateMission: boolean;
  flightData: ReturnType<typeof useFlightProvider>['flightData'];
};

export function useDronesPageViewModel(): DronesPageViewModel {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const droneNotFoundRefetchCount = useRef(0);
  const optimisticRefreshedRef = useRef<string | undefined>(undefined);
  const endedSuppressTimersRef = useRef<Record<string, number>>({});
  // Per-serial: was the selected drone observed actively flying (live status MISSION) during this
  // selection? Lets us recognise the MISSION → IDLE finish even when the backend leaves a stale
  // currentOrderId on the just-landed drone.
  const seenSelectedMissionRef = useRef<Record<string, boolean>>({});
  // Latest drones list for use inside timers (closures would otherwise capture a stale snapshot).
  const allDronesRef = useRef<DroneEntity[]>([]);

  const location = useSelector(selectMapLocation);
  const selectedFromRedux = useSelector(selectSelectedDrone);
  const optimisticMissionSerials = useSelector(selectOptimisticMissionSerials);
  const endedMissionSerials = useSelector(selectEndedMissionSerials);

  const paramsDroneSerial = dronesPageDroneSerialFromUrl(searchParams);
  const paramsMissionId = searchParams.get('missionId');
  const paramsOrderId = searchParams.get('orderId');

  const {
    allDrones,
    visibleDrones,
    idleDrones,
    missionDrones,
    updateRequiredDrones,
    refetch: refetchDrones,
    isLoading: isDronesLoading,
  } = useDronesPageDrones();
  allDronesRef.current = allDrones;
  const { flightData } = useFlightProvider();

  const flyingDrones = useFlyingDrones(missionDrones, flightData);
  const { rotations } = useDroneRotations(flightData);

  const {
    selectedDrone,
    isOnMission: isMissionPhaseFromSelection,
    isOptimisticallyOnMission,
    isStrictMission: isStrictMissionFromSelection,
  } = useDroneSelection(
    selectedFromRedux,
    flyingDrones,
    missionDrones,
    idleDrones,
    visibleDrones,
    optimisticMissionSerials,
  );

  const selectedSerial = paramsDroneSerial ?? selectedFromRedux?.serialNumber ?? null;
  // Mission was just ended for the selected drone (END_MISSION SUCCESS). Force the idle view and
  // suppress the route until REST reflects IDLE, so the route doesn't revive from lagging data.
  const selectedMissionEnded = Boolean(
    selectedSerial && endedMissionSerials.includes(selectedSerial),
  );
  // Mission ended: also drop the strict-mission flag so SelectedDrone hides Go Home / Winch Down
  // and shows only Take Off, even while live status still lags on MISSION.
  const isStrictMission = !selectedMissionEnded && isStrictMissionFromSelection;
  const trustedDroneMissionId = selectedMissionEnded
    ? undefined
    : trustedMissionIdFromDrone(selectedDrone);
  const serialInMissionBucket = isSerialInMissionBucket(selectedSerial, missionDrones);
  const hasActiveOrderOnSelected =
    !selectedMissionEnded &&
    selectedDrone?.currentOrderId != null &&
    Number(selectedDrone.currentOrderId) !== 0;
  const isSelectedIdle =
    selectedMissionEnded ||
    (!serialInMissionBucket &&
      !hasActiveOrderOnSelected &&
      !paramsMissionId &&
      (selectedDrone?.status === DroneStatusType.IDLE ||
        (selectedDrone != null && isUpdateRequiredStatus(selectedDrone.status))) &&
      !isOptimisticallyOnMission);

  const routeMissionIdFromUrl = urlMissionIdForDronesRoute(
    isSelectedIdle,
    paramsMissionId,
    trustedDroneMissionId,
  );

  const orderId = selectedMissionEnded
    ? null
    : paramsOrderId || selectedDrone?.currentOrderId || null;
  const hasOrderBackedMission = Boolean(orderId || paramsMissionId || routeMissionIdFromUrl);

  const { data: order, isLoading: isOrderLoading } = useGetOrderQuery(
    orderId == null ? skipToken : orderId,
    { refetchOnFocus: false, refetchOnMountOrArgChange: false },
  );

  const orderMissionIdForRoute =
    orderId == null ? undefined : missionIdFromOrderForActiveRoute(order);

  const effectiveMissionId = resolveEffectiveMissionId({
    isSelectedIdle,
    trustedDroneMissionId,
    orderMissionIdForRoute,
    routeMissionIdFromUrl,
  });

  const { data: missionData } = useGetOrderMissionQuery(effectiveMissionId ?? undefined, {
    skip: !effectiveMissionId,
  });

  const missionMatches = missionDataMatchesDrone(
    missionData,
    effectiveMissionId,
    selectedSerial,
  );
  const missionHasDeliveryOrders = missionMatches && (missionData?.orders?.length ?? 0) > 0;
  const isFreeFlight =
    isStrictMission && !hasOrderBackedMission && !missionHasDeliveryOrders;

  const mapMode = resolveDronesMapMode({
    effectiveMissionId,
    hasSelectedDrone: Boolean(selectedDrone),
    isFreeFlight,
    missionCacheMatches: !missionData || missionMatches,
  });

  const isOnMission =
    !selectedMissionEnded &&
    (isMissionPhaseFromSelection ||
      Boolean(routeMissionIdFromUrl) ||
      Boolean(paramsMissionId && paramsDroneSerial));
  const isFlying = isOnMission || isSerialInMissionBucket(selectedSerial, missionDrones);
  const prevIsFlying = usePrevious(isFlying) ?? false;
  const sidebarMissionId = isOnMission ? effectiveMissionId : null;

  const displayCreateMission = Boolean(location && selectedFromRedux && !isFlying);

  const enRouteFallbackPosition = React.useMemo((): google.maps.LatLngLiteral | null => {
    const lat = Number(selectedFromRedux?.latitude);
    const lng = Number(selectedFromRedux?.longitude);
    return Number.isNaN(lat) || Number.isNaN(lng) ? null : { lat, lng };
  }, [selectedFromRedux?.latitude, selectedFromRedux?.longitude]);

  React.useEffect(() => {
    if (paramsDroneSerial || !selectedDrone) return;
    dispatch(setSelectedDrone(null));
  }, [paramsDroneSerial, selectedDrone, dispatch]);

  React.useEffect(() => {
    if (!paramsDroneSerial || isDronesLoading) return;
    // Mission just ended for this serial — keep the idle selection set by clearMissionRouteContext;
    // don't re-inject the still-lagging MISSION row from the list.
    if (endedMissionSerials.includes(paramsDroneSerial)) return;

    const foundDrone = visibleDrones.find((d) => d.serialNumber === paramsDroneSerial);

    if (!foundDrone) {
      // The drone exists but needs firmware: it is deliberately kept out of the page's operable
      // lists, so drop the selection at once instead of burning refetches on a drone that will
      // never appear there.
      const knownDrone = allDrones.find((d) => d.serialNumber === paramsDroneSerial);
      if (knownDrone && isUpdateRequiredDrone(knownDrone)) {
        droneNotFoundRefetchCount.current = 0;
        setSearchParams({});
        dispatch(setSelectedDrone(null));
        return;
      }

      if (droneNotFoundRefetchCount.current < 5) {
        droneNotFoundRefetchCount.current += 1;
        refetchDrones();
        return;
      }

      setSearchParams({});
      dispatch(setSelectedDrone(null));
      return;
    }
    droneNotFoundRefetchCount.current = 0;

    const expectedOrderId = paramsOrderId ? Number(paramsOrderId) : foundDrone.currentOrderId;
    const shouldPreferMissionRow =
      foundDrone.status === DroneStatusType.MISSION &&
      selectedFromRedux?.status === DroneStatusType.IDLE;
    const needsUpdate =
      selectedFromRedux?.serialNumber !== paramsDroneSerial || shouldPreferMissionRow;

    if (!needsUpdate) return;

    const lat = Number(foundDrone.latitude);
    const lng = Number(foundDrone.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      dispatch(setMapLocation({ latitude: lat, longitude: lng }));
    }
    dispatch(setSelectedDrone({ ...foundDrone, currentOrderId: expectedOrderId }));
  }, [
    paramsDroneSerial,
    paramsOrderId,
    visibleDrones,
    allDrones,
    selectedFromRedux,
    dispatch,
    refetchDrones,
    isDronesLoading,
    setSearchParams,
    endedMissionSerials,
  ]);

  // Freshly created mission: once we're actually showing this drone on mission, refresh the
  // drones list in the background (data persists during fetch, so no flicker) to pull its real
  // status/order. Runs once per optimistic serial — the navigation itself stays untouched.
  React.useEffect(() => {
    if (!selectedSerial || !isOnMission) return;
    if (!optimisticMissionSerials.includes(selectedSerial)) return;
    if (optimisticRefreshedRef.current === selectedSerial) return;
    optimisticRefreshedRef.current = selectedSerial;
    refetchDrones();
  }, [optimisticMissionSerials, selectedSerial, isOnMission, refetchDrones]);

  React.useEffect(() => {
    if (optimisticMissionSerials.length === 0) return;
    // Drop a serial's optimistic flag only once REST has caught up for THAT drone (flying or
    // order assigned) — real status/currentOrderId then keeps it on mission. A stale
    // IDLE-without-order row must NOT clear it, or the drone flashes idle in the lists.
    // Per-serial so launching a mission on one drone never resets another's optimistic state.
    const caughtUp = optimisticMissionSerials.filter((serial) => {
      const live = allDrones.find((d) => d.serialNumber === serial);
      if (!live) return false;
      const hasActiveOrder =
        live.currentOrderId != null && Number(live.currentOrderId) !== 0;
      return live.status === DroneStatusType.MISSION || hasActiveOrder;
    });
    if (caughtUp.length > 0) {
      dispatch(removeOptimisticMissionSerials(caughtUp));
    }
  }, [optimisticMissionSerials, allDrones, dispatch]);

  React.useEffect(() => {
    if (endedMissionSerials.length === 0) return;
    // Stop suppressing a drone's mission once REST confirms it's actually IDLE (no active order),
    // or it's gone from the list. Until then the suppression keeps the route hidden / drone idle.
    const confirmedIdle = endedMissionSerials.filter((serial) => {
      const live = allDrones.find((d) => d.serialNumber === serial);
      // Keep suppressing while the drone is temporarily absent (refetch / status-filter change):
      // lifting here would let a still-lagging stale order revive the route on re-select.
      if (!live) return false;
      const hasActiveOrder =
        live.currentOrderId != null && Number(live.currentOrderId) !== 0;
      return live.status === DroneStatusType.IDLE && !hasActiveOrder;
    });
    if (confirmedIdle.length > 0) {
      dispatch(removeEndedMissionSerials(confirmedIdle));
    }
  }, [endedMissionSerials, allDrones, dispatch]);

  React.useEffect(() => {
    const timers = endedSuppressTimersRef.current;
    // Arm a one-shot safety timeout per suppressed serial: if REST hasn't confirmed IDLE by then
    // (the IDLE-confirm effect above would have cleared it), lift the suppression anyway.
    endedMissionSerials.forEach((serial) => {
      if (timers[serial] != null) return;
      timers[serial] = window.setTimeout(() => {
        delete timers[serial];
        const live = allDronesRef.current.find((d) => d.serialNumber === serial);
        // Only force-lift when the drone is NOT confirmed idle (e.g. End Mission failed and it's
        // still flying). A genuinely landed drone with a lingering order must stay suppressed —
        // the IDLE-confirm effect lifts it once REST finally drops the order.
        if (!live || live.status !== DroneStatusType.IDLE) {
          dispatch(removeEndedMissionSerials([serial]));
        }
      }, END_MISSION_SUPPRESS_TIMEOUT_MS);
    });
    // Cancel timers for serials already lifted (so a re-added flag gets a fresh full window).
    Object.keys(timers).forEach((serial) => {
      if (!endedMissionSerials.includes(serial)) {
        window.clearTimeout(timers[serial]);
        delete timers[serial];
      }
    });
  }, [endedMissionSerials, dispatch]);

  React.useEffect(() => {
    const timers = endedSuppressTimersRef.current;
    return () => {
      Object.values(timers).forEach((id) => window.clearTimeout(id));
    };
  }, []);

  // Mission finished naturally (no End Mission click): the drone flips MISSION → IDLE on its own.
  // Nothing clears the route context then, so a stale missionId/orderId (URL, redux selection, or a
  // currentOrderId the backend leaves on the just-landed drone) keeps resolving the old route — the
  // small status label reads IDLE while the main interface still shows the mission. Detect the
  // finish and clear/suppress that context so both views go idle together.
  React.useEffect(() => {
    if (!selectedSerial) return;
    if (optimisticMissionSerials.includes(selectedSerial)) return;
    if (endedMissionSerials.includes(selectedSerial)) return;
    const live = allDrones.find((d) => d.serialNumber === selectedSerial);
    if (!live) return;

    if (live.status === DroneStatusType.MISSION) {
      seenSelectedMissionRef.current[selectedSerial] = true;
      return;
    }
    if (live.status !== DroneStatusType.IDLE) return;

    const liveHasActiveOrder =
      live.currentOrderId != null && Number(live.currentOrderId) !== 0;
    const finishedFlying = seenSelectedMissionRef.current[selectedSerial] === true;
    // A pre-takeoff drone (idle + assigned order we never saw flying) keeps its planned route.
    // Treat as finished only when the drone has no active order, or it just landed from a flight.
    if (liveHasActiveOrder && !finishedFlying) return;

    if (paramsMissionId || paramsOrderId) {
      const next = new URLSearchParams(searchParams);
      next.delete('missionId');
      next.delete('orderId');
      setSearchParams(next, { replace: true });
    }
    if (selectedFromRedux && (selectedFromRedux.missionId || selectedFromRedux.currentOrderId)) {
      dispatch(
        setSelectedDrone({
          ...selectedFromRedux,
          missionId: undefined,
          currentOrderId: undefined,
        }),
      );
    }
    // The backend still reporting a stale order on the landed drone would revive the route via the
    // merge/order path even after we clear URL + redux. Suppress it (same machinery as End Mission)
    // until REST clears the order; the IDLE-confirm effect lifts it once the order is gone.
    if (liveHasActiveOrder) {
      dispatch(addEndedMissionSerial(selectedSerial));
    }
    seenSelectedMissionRef.current[selectedSerial] = false;
  }, [
    selectedSerial,
    allDrones,
    optimisticMissionSerials,
    endedMissionSerials,
    paramsMissionId,
    paramsOrderId,
    searchParams,
    setSearchParams,
    selectedFromRedux,
    dispatch,
  ]);

  React.useEffect(() => {
    if (!isSelectedIdle || !paramsMissionId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('missionId');
    setSearchParams(next, { replace: true });
  }, [isSelectedIdle, paramsMissionId, searchParams, setSearchParams]);

  return {
    allDrones,
    idleDrones,
    missionDrones,
    updateRequiredDrones,
    flyingDrones,
    selectedFromRedux,
    selectedDrone,
    selectedSerial,
    location,
    mapMode,
    effectiveMissionId,
    sidebarMissionId,
    orderId,
    order,
    isOrderLoading,
    isOnMission,
    isStrictMission,
    isFlying,
    isOptimisticallyOnMission,
    prevIsFlying,
    rotations,
    enRouteFallbackPosition,
    displayCreateMission,
    flightData,
  };
}
